import React from 'react';
import {
    Trash2,
    AlertTriangle,
    ExternalLink,
    Eye,
    Paperclip,
    Shield,
    KeyRound,
    Building2,
    Calendar,
    BarChart3,
} from 'lucide-react';
import { INotification } from '../../services/api/notificationService';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

interface NotificationCardBaseProps {
    notification: INotification;
    className?: string;
}

interface AdminNotificationCardProps extends NotificationCardBaseProps {
    variant: 'admin';
    onDelete: (uid: string) => void;
    onPreview?: (notification: INotification) => void;
    isDeleting?: boolean;
}

interface DisplayNotificationCardProps extends NotificationCardBaseProps {
    variant: 'display';
    onClick: (notification: INotification) => void;
}

export type NotificationCardProps =
    | AdminNotificationCardProps
    | DisplayNotificationCardProps;

const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const stripHtml = (html: string) =>
    html
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const NotificationCard: React.FC<NotificationCardProps> = (props) => {
    const { notification, variant, className } = props;

    const isAdmin = variant === 'admin';
    const isDisplay = variant === 'display';
    const totalCount = notification.totalCount ?? 0;
    const shownCount = notification.shownCount ?? 0;
    const notShownCount = Math.max(totalCount - shownCount, 0);
    const statusCounts = [
        { label: 'Unread', value: `${notification.count ?? 0}/${totalCount}` },
        { label: 'Not shown', value: notShownCount },
        { label: 'Shown', value: shownCount },
        { label: 'Dismissed', value: notification.dismissedCount ?? 0 },
        { label: 'Read', value: notification.readCount ?? 0 },
    ];

    return (
        <Card
            className={cn(
                'border-white/50',
                'transition-colors',
                isDisplay && 'cursor-pointer hover:bg-muted/50',
                isAdmin &&
                    (props as AdminNotificationCardProps).isDeleting &&
                    'opacity-60',
                className
            )}
            onClick={
                isDisplay
                    ? () =>
                          (props as DisplayNotificationCardProps).onClick(
                              notification
                          )
                    : undefined
            }
        >
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    {/* Main content */}
                    <div className="min-w-0 flex-1 space-y-1.5">
                        {/* Title row */}
                        <p className="font-medium leading-snug line-clamp-1">
                            {notification.title || 'Untitled'}
                        </p>

                        {/* Description preview (display only) */}
                        {isDisplay && notification.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                                {stripHtml(notification.description)}
                            </p>
                        )}

                        {/* Meta row */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {notification.video_id && (
                                <Badge
                                    variant="secondary"
                                    className="gap-1 text-[10px] px-1.5 py-0"
                                >
                                    <Paperclip className="h-3 w-3" />
                                    Attachment
                                </Badge>
                            )}
                            {notification.action?.type === 'external_link' && (
                                <Badge
                                    variant="secondary"
                                    className="gap-1 text-[10px] px-1.5 py-0"
                                >
                                    <ExternalLink className="h-3 w-3" />
                                    {notification.action.label || 'Link'}
                                </Badge>
                            )}
                            {notification.createdAt && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(notification.createdAt)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Admin controls */}
                    {isAdmin && (
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                            {/* Badges */}
                            <div className="flex flex-wrap items-center gap-1 justify-end">
                                {notification.isImportant && (
                                    <Badge
                                        variant="default"
                                        className="gap-1 text-[10px] px-1.5 py-0"
                                    >
                                        <AlertTriangle className="h-3 w-3" />
                                        Important
                                    </Badge>
                                )}
                                {notification.targetRights &&
                                    notification.targetRights.length > 0 && (
                                        <Badge
                                            variant="outline"
                                            className="gap-1 text-[10px] px-1.5 py-0"
                                        >
                                            <Shield className="h-3 w-3" />
                                            {
                                                notification.targetRights.length
                                            }{' '}
                                            rights
                                        </Badge>
                                    )}
                                {notification.targetPermissions &&
                                    notification.targetPermissions.length >
                                        0 && (
                                        <Badge
                                            variant="outline"
                                            className="gap-1 text-[10px] px-1.5 py-0"
                                        >
                                            <KeyRound className="h-3 w-3" />
                                            {
                                                notification.targetPermissions
                                                    .length
                                            }{' '}
                                            perms
                                        </Badge>
                                    )}
                                {notification.targetDepartmentIds &&
                                    notification.targetDepartmentIds.length > 0 && (
                                        <Badge
                                            variant="outline"
                                            className="gap-1 text-[10px] px-1.5 py-0"
                                        >
                                            <Building2 className="h-3 w-3" />
                                            {notification.targetDepartmentIds.length}{' '}
                                            depts
                                        </Badge>
                                    )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center gap-1">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                            aria-label="View notification status"
                                        >
                                            <BarChart3 className="h-4 w-4" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="end"
                                        className="w-56 space-y-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <p className="text-sm font-medium">
                                            Status
                                        </p>
                                        <div className="space-y-1.5">
                                            {statusCounts.map((status) => (
                                                <div
                                                    key={status.label}
                                                    className="flex items-center justify-between gap-4 text-xs"
                                                >
                                                    <span className="text-muted-foreground">
                                                        {status.label}
                                                    </span>
                                                    <span className="font-medium tabular-nums">
                                                        {status.value}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                {(props as AdminNotificationCardProps)
                                    .onPreview && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            (
                                                props as AdminNotificationCardProps
                                            ).onPreview!(notification);
                                        }}
                                        aria-label="Preview notification"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        (
                                            props as AdminNotificationCardProps
                                        ).onDelete(notification._id!);
                                    }}
                                    disabled={
                                        (props as AdminNotificationCardProps)
                                            .isDeleting
                                    }
                                    aria-label="Delete notification"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default NotificationCard;
